import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  X,
  Mic,
  Square,
  Sparkles,
  Plus,
  Trash2,
  PenTool,
  Eraser,
  Receipt,
  Pill,
  Loader2,
  Printer,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Portal from "@/components/Portal";
import MedAutocomplete from "@/components/MedAutocomplete";
import PrescriptionSlip from "@/components/PrescriptionSlip";
import { useAuth as _useAuthForBranch } from "@/context/AuthContext";

const FREQ_OPTIONS = [
  { value: "", label: "Frequency…" },
  { value: "Morning", label: "Morning (രാവിലെ)" },
  { value: "Afternoon", label: "Afternoon" },
  { value: "Evening", label: "Evening (വൈകുന്നേരം)" },
  { value: "Night / Bedtime", label: "Night / Bedtime" },
  { value: "Morning & Evening", label: "Morning & Evening (BD)" },
  { value: "Twice daily", label: "Twice daily (BD)" },
  { value: "Thrice daily", label: "Thrice daily (TDS)" },
  { value: "Four times a day", label: "Four times a day (QDS)" },
  { value: "Before food", label: "Before food" },
  { value: "After food", label: "After food" },
  { value: "Empty stomach", label: "Empty stomach" },
  { value: "SOS", label: "SOS (as needed)" },
  { value: "As directed", label: "As directed by doctor" },
];

const BLANK_ITEM = { drug: "", dose: "", frequency: "", duration: "", sku: null, qty: 1, price: null };

export default function ConsultationPanel({ patient, onClose, onSubmitted }) {
  const { branch } = _useAuthForBranch();
  const [medOptions, setMedOptions] = useState([]);

  useEffect(() => {
    if (!branch?.code) return;
    api.get("/inventory", { params: { branch_code: branch.code } })
      .then(r => setMedOptions(r.data || []))
      .catch(() => setMedOptions([]));
  }, [branch?.code]);
  const [diagnosis, setDiagnosis] = useState("");
  const [voiceMode, setVoiceMode] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [aiSummary, setAiSummary] = useState(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [summarizing, setSummarizing] = useState(false);

  const [items, setItems] = useState([{ ...BLANK_ITEM }]);
  const [instructions, setInstructions] = useState("");
  const [sketchDataUrl, setSketchDataUrl] = useState(null);
  const [slipPatient, setSlipPatient] = useState(null);

  const [sendTo, setSendTo] = useState("billing");
  const [submitting, setSubmitting] = useState(false);

  // Voice recording refs
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime });
        await sendForTranscription(blob, mime);
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch (err) {
      toast.error("Microphone access denied — allow it in your browser");
    }
  };

  const stopRecording = () => {
    setRecording(false);
    if (recRef.current && recRef.current.state !== "inactive") {
      recRef.current.stop();
    }
  };

  const sendForTranscription = async (blob, mime) => {
    setTranscribing(true);
    try {
      const ext = mime.includes("webm") ? "webm" : "mp4";
      const form = new FormData();
      form.append("audio", blob, `consult.${ext}`);
      const { data } = await api.post(
        `/consultations/${patient.id}/transcribe`,
        form,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 }
      );
      const t = (data.transcript || "").trim();
      setTranscript(t);
      toast.success("Transcribed");
      if (t) await runSummarize(t);
    } catch (err) {
      toast.error(formatApiError(err, "Transcription failed"));
    } finally {
      setTranscribing(false);
    }
  };

  const runSummarize = async (text) => {
    setSummarizing(true);
    try {
      const { data } = await api.post(
        `/consultations/${patient.id}/summarize`,
        { transcript: text },
        { timeout: 120000 }
      );
      const s = data.summary || {};
      setAiSummary(s);
      // Compose editable diagnosis text
      const composed = [
        s.chief_complaint && `Chief complaint: ${s.chief_complaint}`,
        s.findings && `Findings: ${s.findings}`,
        s.diagnosis && `Diagnosis: ${s.diagnosis}`,
        s.advice && `Advice: ${s.advice}`,
      ]
        .filter(Boolean)
        .join("\n");
      setDiagnosis((prev) => (prev ? `${prev}\n\n${composed}` : composed));
      // Seed prescription items from hints
      const hints = Array.isArray(s.prescription_hints) ? s.prescription_hints : [];
      if (hints.length) {
        setItems((prev) => {
          const merged = prev.filter((it) => it.drug.trim());
          hints.forEach((h) => merged.push({ ...BLANK_ITEM, drug: h }));
          return merged.length ? merged : [{ ...BLANK_ITEM }];
        });
      }
      toast.success("AI summary drafted — please verify");
    } catch (err) {
      toast.error(formatApiError(err, "Summarization failed"));
    } finally {
      setSummarizing(false);
    }
  };

  // ---- Prescription items ----
  const setItem = (i, k, v) =>
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addItem = () => setItems((prev) => [...prev, { ...BLANK_ITEM }]);
  const removeItem = (i) =>
    setItems((prev) => (prev.length === 1 ? [{ ...BLANK_ITEM }] : prev.filter((_, idx) => idx !== i)));

  // ---- Submit ----
  const submit = async () => {
    if (!diagnosis.trim()) {
      toast.error("Diagnosis note is required");
      return;
    }
    const cleanItems = items
      .map((it) => ({
        drug: it.drug.trim(),
        dose: (it.dose || "").trim(),
        frequency: (it.frequency || "").trim(),
        duration: (it.duration || "").trim(),
        sku: it.sku || null,
        qty: Number(it.qty) > 0 ? Number(it.qty) : 1,
        price: it.price != null ? Number(it.price) : null,
      }))
      .filter((it) => it.drug);
    setSubmitting(true);
    try {
      const { data } = await api.post(`/consultations/${patient.id}/submit`, {
        diagnosis_text: diagnosis,
        transcript,
        ai_summary: aiSummary,
        prescription: {
          items: cleanItems,
          instructions,
          sketch_data_url: sketchDataUrl,
        },
        send_to: sendTo,
      });
      toast.success(
        sendTo === "billing"
          ? "Sent to pharmacy billing"
          : "Sent straight to pharmacy"
      );
      // Show printable slip so patient can walk out with the takeaway
      setSlipPatient(data);
    } catch (err) {
      toast.error(formatApiError(err, "Could not submit"));
    } finally {
      setSubmitting(false);
    }
  };

  const closeSlipAndPanel = () => {
    const dataForParent = slipPatient;
    setSlipPatient(null);
    onSubmitted && onSubmitted(dataForParent);
  };

  return (
    <Portal>
    <div
      className="fixed inset-0 bg-[#2A2624]/55 z-40 flex items-end sm:items-stretch sm:justify-end"
      onClick={() => !submitting && onClose()}
      data-testid="consultation-overlay"
    >
      <div
        className="bg-white w-full sm:max-w-3xl h-full sm:h-screen overflow-y-auto animate-fade-up border-l border-sand"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white border-b border-sand px-6 py-4 flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5652] mb-1">
              {patient.mrn} · Token {patient.op_token} · Age {patient.age} · {patient.gender}
            </div>
            <div className="font-display text-2xl text-forest leading-tight">
              Consultation · {patient.name}
            </div>
            {patient.complaint && (
              <div className="text-sm text-[#5C5652] mt-1">
                Complaint: {patient.complaint}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            data-testid="consultation-close"
            className="h-9 w-9 rounded-lg border border-sand hover:bg-[#F9F6F0] flex items-center justify-center"
            aria-label="Close"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="px-6 py-6 space-y-8" data-testid="consultation-panel">
          {/* Diagnosis */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#5C5652] ornament-dot">
                Diagnosis note
              </div>
              <label className="inline-flex items-center gap-2 text-sm" data-testid="voice-mode-toggle-label">
                <span className="text-[#5C5652]">AI voice mode (Malayalam)</span>
                <button
                  type="button"
                  data-testid="voice-mode-toggle"
                  onClick={() => setVoiceMode((v) => !v)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    voiceMode ? "bg-forest" : "bg-[#E8E3D9]"
                  }`}
                  aria-pressed={voiceMode}
                >
                  <span
                    className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
                      voiceMode ? "left-5" : "left-0.5"
                    }`}
                  />
                </button>
              </label>
            </div>

            {voiceMode && (
              <div className="mb-3 p-4 rounded-lg border border-sand bg-[#F9F6F0]" data-testid="voice-panel">
                <div className="flex items-center gap-3">
                  {recording ? (
                    <button
                      onClick={stopRecording}
                      data-testid="voice-stop"
                      className="h-11 px-4 rounded-lg bg-[#C25946] text-white font-semibold inline-flex items-center gap-2 hover:opacity-90"
                    >
                      <Square className="h-4 w-4 fill-white" strokeWidth={1.5} />
                      Stop recording
                    </button>
                  ) : (
                    <button
                      onClick={startRecording}
                      disabled={transcribing || summarizing}
                      data-testid="voice-record"
                      className="h-11 px-4 rounded-lg bg-forest text-white font-semibold inline-flex items-center gap-2 hover:bg-forest-hover disabled:opacity-60"
                    >
                      <Mic className="h-4 w-4" strokeWidth={1.75} />
                      Record dictation
                    </button>
                  )}
                  <div className="text-xs text-[#5C5652]">
                    {recording && "Recording… tap Stop when done"}
                    {transcribing && (
                      <span className="inline-flex items-center gap-1">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.75} />
                        Transcribing with Whisper…
                      </span>
                    )}
                    {summarizing && (
                      <span className="inline-flex items-center gap-1">
                        <Sparkles className="h-3.5 w-3.5" strokeWidth={1.75} />
                        Summarising with GPT-5.4…
                      </span>
                    )}
                    {!recording && !transcribing && !summarizing && !transcript && (
                      <>Speak in Malayalam or English — the AI will draft the note.</>
                    )}
                  </div>
                </div>
                {transcript && (
                  <details className="mt-3 text-xs text-[#5C5652]" data-testid="voice-transcript-details">
                    <summary className="cursor-pointer font-semibold">View raw transcript</summary>
                    <div className="mt-2 whitespace-pre-wrap bg-white border border-sand rounded p-3">
                      {transcript}
                    </div>
                  </details>
                )}
              </div>
            )}

            <textarea
              data-testid="diagnosis-textarea"
              value={diagnosis}
              onChange={(e) => setDiagnosis(e.target.value)}
              rows={6}
              placeholder="Chief complaint, findings, diagnosis and advice — edit AI draft as needed."
              className="w-full px-3 py-3 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 focus:border-[#1A4331] resize-y"
            />
          </section>

          {/* Prescription — typed rows */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#5C5652] ornament-dot">
                Prescription
              </div>
              <button
                onClick={addItem}
                data-testid="add-med-row"
                className="text-sm text-forest font-semibold inline-flex items-center gap-1 hover:underline"
              >
                <Plus className="h-3.5 w-3.5" strokeWidth={2} /> Add medicine
              </button>
            </div>

            <div className="space-y-2" data-testid="rx-items">
              {medOptions.length === 0 && (
                <div
                  data-testid="rx-no-stock-hint"
                  className="text-[11px] text-[#8A5F1B] bg-[#E5A93D]/10 border border-[#E5A93D]/40 rounded px-3 py-2"
                >
                  No stock is loaded for this branch yet — medicine suggestions will be empty. Ask the manager to add inventory in <span className="font-semibold">Manager · Stock</span>. You can still type medicine names manually.
                </div>
              )}
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-2 md:grid-cols-[1.6fr_0.8fr_1.3fr_0.9fr_0.6fr_auto] gap-2">
                  <div className="col-span-2 md:col-span-1">
                    <MedAutocomplete
                      testId={`rx-drug-${i}`}
                      value={it.drug}
                      onChange={(v) =>
                        setItems((prev) =>
                          prev.map((p, idx) =>
                            idx === i ? { ...p, drug: v, sku: null, price: null } : p
                          )
                        )
                      }
                      onPick={(inv) =>
                        setItems((prev) =>
                          prev.map((p, idx) =>
                            idx === i
                              ? {
                                  ...p,
                                  drug: inv.name,
                                  sku: inv.sku,
                                  price: inv.selling_price,
                                  qty: p.qty && Number(p.qty) > 0 ? p.qty : 1,
                                }
                              : p
                          )
                        )
                      }
                      options={medOptions}
                      placeholder="Medicine (type to search stock)"
                    />
                  </div>
                  <input
                    data-testid={`rx-dose-${i}`}
                    value={it.dose}
                    onChange={(e) => setItem(i, "dose", e.target.value)}
                    placeholder="Dose (e.g. 1 tab, 5 ml)"
                    className="h-10 px-3 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 min-w-0 text-sm"
                  />
                  <select
                    data-testid={`rx-freq-${i}`}
                    value={it.frequency}
                    onChange={(e) => setItem(i, "frequency", e.target.value)}
                    className="h-10 px-2 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 min-w-0 text-sm text-[#2A2624]"
                  >
                    {FREQ_OPTIONS.map((f) => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                  <input
                    data-testid={`rx-dur-${i}`}
                    value={it.duration}
                    onChange={(e) => setItem(i, "duration", e.target.value)}
                    placeholder="Duration"
                    className="h-10 px-3 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 min-w-0 text-sm"
                  />
                  <div className="relative">
                    <input
                      data-testid={`rx-qty-${i}`}
                      type="number"
                      min="1"
                      value={it.qty}
                      onChange={(e) => setItem(i, "qty", Number(e.target.value) || 1)}
                      placeholder="Qty"
                      title="Units to dispense (this flows to billing)"
                      className="h-10 pl-3 pr-8 w-full rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 min-w-0 text-sm"
                    />
                    <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-bold text-[#5C5652] tracking-wider">QTY</span>
                  </div>
                  <button
                    onClick={() => removeItem(i)}
                    data-testid={`rx-remove-${i}`}
                    className="h-10 w-10 rounded-lg border border-sand text-[#5C5652] hover:bg-[#F9F6F0] flex items-center justify-center"
                    aria-label="Remove row"
                  >
                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                </div>
              ))}
            </div>

            <textarea
              data-testid="rx-instructions"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={2}
              placeholder="Instructions to the patient (e.g., take before food, avoid cold drinks)"
              className="mt-3 w-full px-3 py-3 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 resize-none"
            />
          </section>

          {/* Sketchpad */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#5C5652] ornament-dot inline-flex items-center gap-2">
                <PenTool className="h-3.5 w-3.5" strokeWidth={1.75} />
                Handwritten sketch / signature (optional)
              </div>
            </div>
            <SketchPad value={sketchDataUrl} onChange={setSketchDataUrl} />
          </section>

          {/* Send to */}
          <section>
            <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#5C5652] mb-3 ornament-dot">
              Route this patient to
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="send-to-picker">
              <button
                onClick={() => setSendTo("billing")}
                data-testid="send-to-billing"
                className={`text-left p-4 rounded-lg border transition-colors ${
                  sendTo === "billing"
                    ? "border-forest bg-[#1A4331]/6"
                    : "border-sand hover:bg-[#F9F6F0]"
                }`}
              >
                <div className="flex items-center gap-2 font-semibold text-forest">
                  <Receipt className="h-4 w-4" strokeWidth={1.75} />
                  Pharmacy billing
                </div>
                <div className="text-xs text-[#5C5652] mt-1">
                  Bill is generated first, SMS sent to the patient, then pharmacy dispenses.
                </div>
              </button>
              <button
                onClick={() => setSendTo("pharmacy")}
                data-testid="send-to-pharmacy"
                className={`text-left p-4 rounded-lg border transition-colors ${
                  sendTo === "pharmacy"
                    ? "border-forest bg-[#1A4331]/6"
                    : "border-sand hover:bg-[#F9F6F0]"
                }`}
              >
                <div className="flex items-center gap-2 font-semibold text-forest">
                  <Pill className="h-4 w-4" strokeWidth={1.75} />
                  Pharmacy directly
                </div>
                <div className="text-xs text-[#5C5652] mt-1">
                  Skip billing — dispense directly (e.g., free samples or follow-up).
                </div>
              </button>
            </div>
          </section>

          {/* Submit bar */}
          <div className="sticky bottom-0 -mx-6 px-6 py-4 border-t border-sand bg-white flex gap-3">
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="consultation-submit"
              className="flex-1 h-12 rounded-lg bg-forest text-white font-semibold hover:bg-forest-hover disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {submitting
                ? "Submitting…"
                : sendTo === "billing"
                  ? "Save & send to billing"
                  : "Save & send to pharmacy"}
            </button>
            <button
              onClick={onClose}
              disabled={submitting}
              className="h-12 px-5 rounded-lg border border-sand text-[#2A2624] font-semibold hover:bg-[#F9F6F0]"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
    {slipPatient && (
      <PrescriptionSlip
        patient={slipPatient}
        branch={branch}
        onClose={closeSlipAndPanel}
      />
    )}
    </Portal>
  );
}

/* -------------------------- Sketchpad canvas -------------------------- */
function SketchPad({ value, onChange }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      img.src = value;
    }
     
  }, []);

  const pos = (e) => {
    const canvas = canvasRef.current;
    const r = canvas.getBoundingClientRect();
    const scaleX = canvas.width / r.width;
    const scaleY = canvas.height / r.height;
    const point = e.touches ? e.touches[0] : e;
    return { x: (point.clientX - r.left) * scaleX, y: (point.clientY - r.top) * scaleY };
  };

  const begin = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = pos(e);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.strokeStyle = "#1A4331";
    ctx.lineWidth = 2.4;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
  };

  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const url = canvasRef.current.toDataURL("image/png");
    onChange(url);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    onChange(null);
  };

  return (
    <div className="p-3 rounded-lg border border-sand bg-[#F9F6F0]" data-testid="sketchpad">
      <canvas
        ref={canvasRef}
        width={900}
        height={280}
        onMouseDown={begin}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={begin}
        onTouchMove={move}
        onTouchEnd={end}
        className="w-full h-56 bg-white rounded border border-dashed border-[#C9C1B0] touch-none cursor-crosshair"
        data-testid="sketchpad-canvas"
      />
      <div className="mt-2 flex justify-between text-xs text-[#5C5652]">
        <span>Draw with mouse or finger.</span>
        <button
          onClick={clear}
          data-testid="sketchpad-clear"
          className="inline-flex items-center gap-1 text-[#C25946] hover:underline font-semibold"
        >
          <Eraser className="h-3 w-3" strokeWidth={2} />
          Clear
        </button>
      </div>
    </div>
  );
}
